import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firestore';
import { usePendingHandovers } from './useHandovers';

/**
 * Lightweight hook that provides real-time badge counts for sidebar navigation.
 *
 * Subscribes to Firestore for:
 * - Active session count (status in ['active', 'handover_requested', 'agent_active'])
 * - Pending handover count (status === 'pending')
 *
 * Only subscribes when a propertyId is provided; cleans up on unmount or property change.
 */
export function useQueueCounts(propertyId: string | undefined) {
  const [sessionCount, setSessionCount] = useState(0);
  const { data: pendingHandovers } = usePendingHandovers(propertyId ?? undefined);

  // Active sessions (property-scoped subcollection)
  useEffect(() => {
    if (!propertyId) {
      setSessionCount(0);
      return;
    }

    const sessionsRef = collection(db, 'properties', propertyId, 'sessions');
    const q = query(
      sessionsRef,
      where('status', 'in', ['active', 'handover_requested', 'agent_active']),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => setSessionCount(snapshot.size),
      () => setSessionCount(0), // Silently reset on permission errors
    );

    return () => unsubscribe();
  }, [propertyId]);

  return { sessionCount, handoverCount: pendingHandovers.length };
}
